// src/services/JourneyService.js

import { getBlueprintForProblem } from "../JourneyBlueprints";
import { getProblemTitleForLang } from "../journeyProblems";
import { normalizeLang } from "../i18n";
import { API_BASE } from "../apiConfig";
import {
  PHASE1_PROBLEM_KEY,
  PHASE1_REFERENCE_18_PROBLEM_KEY,
  isPhaseOneOtherIntakeSnapshot,
} from "../phase1/config";

const STORAGE_PREFIX = "innervoice_journey_v3";
const JOURNEY_LANGS = ["he", "en", "es"];
const BREAKUP_REFERENCE_ALIASES = new Set([
  "rel_breakup_fresh_pain",
  PHASE1_PROBLEM_KEY,
  PHASE1_REFERENCE_18_PROBLEM_KEY,
]);

export function normalizeJourneyProblemKey(problemKey) {
  const safeKey = String(problemKey || "").trim();
  if (!safeKey) return "";
  return BREAKUP_REFERENCE_ALIASES.has(safeKey) ? PHASE1_REFERENCE_18_PROBLEM_KEY : safeKey;
}

function getJourneyProblemKeyVariants(problemKey) {
  const canonicalKey = normalizeJourneyProblemKey(problemKey);
  if (canonicalKey === PHASE1_REFERENCE_18_PROBLEM_KEY) {
    return uniqueList([canonicalKey, "rel_breakup_fresh_pain", PHASE1_PROBLEM_KEY]);
  }
  return [canonicalKey];
}

function findStoredValueForProblem(getKeyFn, langs, problemKey) {
  for (const candidateProblemKey of getJourneyProblemKeyVariants(problemKey)) {
    const found = findStoredValue((lang) => getKeyFn(lang, candidateProblemKey), langs);
    if (found) return { ...found, problemKey: candidateProblemKey };
  }
  return null;
}

function uniqueList(items) {
  return Array.from(new Set(items.filter(Boolean)));
}

function candidateLangs(lang) {
  const safe = normalizeLang(lang);
  return uniqueList([safe, ...JOURNEY_LANGS]);
}

function findStoredValue(getKeyFn, langs) {
  for (const lang of langs) {
    const key = getKeyFn(lang);
    const raw = localStorage.getItem(key);
    if (raw) return { key, raw, lang };
  }
  return null;
}

function metaKey(lang, problemKey) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::meta`;
}

function intakeKey(lang, problemKey) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::intake`;
}

function dayKey(lang, problemKey, day) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::day_${day}`;
}

function aiSessionKey(lang, problemKey) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::ai_session`;
}

function signalsKey(lang, problemKey) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::signals_latest`;
}

function normalizeRunId(runId) {
  return String(runId || "").trim();
}

function runIntakeKey(lang, problemKey, runId) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::run_${normalizeRunId(runId)}::intake`;
}

function runDayKey(lang, problemKey, runId, day) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::run_${normalizeRunId(runId)}::day_${day}`;
}

function runAiSessionKey(lang, problemKey, runId) {
  return `${STORAGE_PREFIX}::${normalizeLang(lang)}::${problemKey}::run_${normalizeRunId(runId)}::ai_session`;
}

function makeRunId() {
  return `run_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function readMeta(lang, problemKey) {
  const safeLang = normalizeLang(lang);
  const canonicalProblemKey = normalizeJourneyProblemKey(problemKey);
  const key = metaKey(safeLang, canonicalProblemKey);
  const storedAny = findStoredValueForProblem(
    (candidateLang, candidateProblemKey) => metaKey(candidateLang, candidateProblemKey),
    candidateLangs(safeLang),
    canonicalProblemKey
  );
  const parsed = safeParse(storedAny?.raw || localStorage.getItem(key), null);
  if (storedAny && storedAny.key !== key && parsed) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          ...parsed,
          lang: safeLang,
          problemKey: canonicalProblemKey,
        })
      );
    } catch {
      // ignore
    }
  }
  return parsed;
}

function writeMeta(lang, problemKey, value) {
  const safeLang = normalizeLang(lang);
  const canonicalProblemKey = normalizeJourneyProblemKey(problemKey);
  const key = metaKey(safeLang, canonicalProblemKey);
  localStorage.setItem(
    key,
    JSON.stringify({
      ...value,
      lang: safeLang,
      problemKey: canonicalProblemKey,
    })
  );
  return value;
}

function safeParse(json, fallback = null) {
  if (!json) return fallback;
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

async function postJson(path, payload) {
  const res = await fetch(`${API_BASE}/api/journeys${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function getJson(path) {
  const res = await fetch(`${API_BASE}/api/journeys${path}`, {
    credentials: "include",
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function postProgress(path, payload) {
  const res = await fetch(`${API_BASE}/api/progress${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function getProgress(path) {
  const res = await fetch(`${API_BASE}/api/progress${path}`, {
    credentials: "include",
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function postSignals(path, payload) {
  const res = await fetch(`${API_BASE}/api/signals${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function getSignals(path) {
  const res = await fetch(`${API_BASE}/api/signals${path}`, {
    credentials: "include",
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function getInsights(path) {
  const res = await fetch(`${API_BASE}/api/insights${path}`, {
    credentials: "include",
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

async function getJournal(path) {
  const res = await fetch(`${API_BASE}/api/journal${path}`, {
    credentials: "include",
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

function buildJournalEntryKey(entry) {
  const q = entry?.questionId || "";
  const t = entry?.text || "";
  const c = entry?.createdAt || "";
  return `${q}::${c}::${t}`.trim();
}

function readLatestSignalsCache(lang, problemKey) {
  const safeLang = normalizeLang(lang);
  const canonicalProblemKey = normalizeJourneyProblemKey(problemKey);
  const key = signalsKey(safeLang, canonicalProblemKey);
  const storedAny = findStoredValueForProblem(
    (candidateLang, candidateProblemKey) => signalsKey(candidateLang, candidateProblemKey),
    candidateLangs(safeLang),
    canonicalProblemKey
  );
  const parsed = safeParse(storedAny?.raw || localStorage.getItem(key), null);
  if (storedAny && storedAny.key !== key && parsed) {
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          ...parsed,
          lang: safeLang,
          problemKey: canonicalProblemKey,
        })
      );
    } catch {
      // ignore
    }
  }
  return parsed;
}

function writeLatestSignalsCache(lang, problemKey, value) {
  const safeLang = normalizeLang(lang);
  const canonicalProblemKey = normalizeJourneyProblemKey(problemKey);
  const key = signalsKey(safeLang, canonicalProblemKey);
  const nextValue = {
    ...(value || {}),
    lang: safeLang,
    problemKey: canonicalProblemKey,
    updatedAt: value?.updatedAt || new Date().toISOString(),
  };
  localStorage.setItem(key, JSON.stringify(nextValue));
  return nextValue;
}

function summarizeIntake(intake = {}) {
  return {
    painNow: intake?.painNow || intake?.painStory || "",
    desiredEndState: intake?.desiredEndState || "",
    oneWord: intake?.oneWord || intake?.one_word || intake?.oneWordNow || "",
    entryGateKey: intake?.entryGateKey || "",
  };
}

function buildRunSnapshot(run, meta, days = {}, intake = null) {
  const totalDays = Number(meta?.totalDays || 0) || 1;
  const completedDays = Object.entries(days)
    .filter(([, state]) => state?.completed)
    .map(([day]) => Number(day));
  const highestCompleted = completedDays.length ? Math.max(...completedDays) : 0;
  const nextDay = Math.min(totalDays, highestCompleted + 1 || 1);
  const isComplete = highestCompleted >= totalDays;
  const isActive = normalizeRunId(meta?.activeRunId) === normalizeRunId(run?.id);
  const status = isComplete ? "completed" : isActive ? "active" : run?.status || "abandoned";
  return {
    ...run,
    totalDays,
    intake: summarizeIntake(intake || run),
    completedDays,
    highestCompleted,
    nextDay,
    progressRatio: totalDays ? highestCompleted / totalDays : 0,
    isComplete,
    status,
  };
}

function resolveRunId(meta, preferredRunId = "") {
  const runs = Array.isArray(meta?.runs) ? meta.runs : [];
  const requested = normalizeRunId(preferredRunId);
  if (requested && runs.some((run) => normalizeRunId(run?.id) === requested)) {
    return requested;
  }
  const active = normalizeRunId(meta?.activeRunId);
  if (active && runs.some((run) => normalizeRunId(run?.id) === active)) {
    return active;
  }
  return normalizeRunId(runs[0]?.id);
}

function syncActiveRunMetaFields(meta, runId) {
  const normalizedRunId = normalizeRunId(runId);
  const anchors = meta?.journeyAnchors && typeof meta.journeyAnchors === "object" ? meta.journeyAnchors : {};
  const activeAnchor = anchors[normalizedRunId] || null;
  return {
    ...meta,
    activeRunId: normalizedRunId,
    journeyAnchorText: String(activeAnchor?.text || "").trim(),
    journeyAnchorDayNumber: Number(activeAnchor?.dayNumber || 1),
  };
}

function ensureMetaRuns(lang, problemKey, meta, totalDays) {
  const safeLang = normalizeLang(lang);
  const canonicalProblemKey = normalizeJourneyProblemKey(problemKey);
  const existingRuns = Array.isArray(meta?.runs)
    ? meta.runs.filter((run) => normalizeRunId(run?.id))
    : [];

  if (existingRuns.length > 0) {
    const activeRunId = resolveRunId(meta, meta?.activeRunId);
    const nextMeta = syncActiveRunMetaFields(
      {
        ...meta,
        runs: existingRuns,
        presenceMoments: Array.isArray(meta?.presenceMoments) ? meta.presenceMoments : [],
      },
      activeRunId
    );
    return {
      changed:
        activeRunId !== normalizeRunId(meta?.activeRunId) ||
        !Array.isArray(meta?.presenceMoments) ||
        nextMeta.journeyAnchorText !== String(meta?.journeyAnchorText || "").trim() ||
        Number(nextMeta.journeyAnchorDayNumber || 1) !== Number(meta?.journeyAnchorDayNumber || 1),
      meta: nextMeta,
    };
  }

  const now = new Date().toISOString();
  const initialRunId = normalizeRunId(meta?.activeRunId) || makeRunId();
  const legacyIntake =
    safeParse(
      findStoredValueForProblem(
        (candidateLang, candidateProblemKey) => intakeKey(candidateLang, candidateProblemKey),
        candidateLangs(safeLang),
        canonicalProblemKey
      )?.raw || localStorage.getItem(intakeKey(safeLang, canonicalProblemKey)),
      null
    ) || null;
  const legacyAiSession =
    findStoredValueForProblem(
      (candidateLang, candidateProblemKey) => aiSessionKey(candidateLang, candidateProblemKey),
      candidateLangs(safeLang),
      canonicalProblemKey
    )?.raw || localStorage.getItem(aiSessionKey(safeLang, canonicalProblemKey));

  if (legacyIntake) {
    try {
      localStorage.setItem(runIntakeKey(safeLang, canonicalProblemKey, initialRunId), JSON.stringify(legacyIntake));
    } catch {
      // ignore
    }
  }
  if (legacyAiSession) {
    try {
      localStorage.setItem(runAiSessionKey(safeLang, canonicalProblemKey, initialRunId), legacyAiSession);
    } catch {
      // ignore
    }
  }

  const legacyDays = {};
  for (let dayNumber = 1; dayNumber <= totalDays; dayNumber += 1) {
    const legacyStored =
      safeParse(
        findStoredValueForProblem(
          (candidateLang, candidateProblemKey) => dayKey(candidateLang, candidateProblemKey, dayNumber),
          candidateLangs(safeLang),
          canonicalProblemKey
        )?.raw || localStorage.getItem(dayKey(safeLang, canonicalProblemKey, dayNumber)),
        null
      ) || null;
    if (!legacyStored) continue;
    legacyDays[dayNumber] = legacyStored;
    try {
      localStorage.setItem(
        runDayKey(safeLang, canonicalProblemKey, initialRunId, dayNumber),
        JSON.stringify(legacyStored)
      );
    } catch {
      // ignore
    }
  }

  const completedDays = Object.entries(legacyDays)
    .filter(([, state]) => state?.completed)
    .map(([dayNumber]) => Number(dayNumber));
  const initialAnchorText = String(meta?.journeyAnchorText || "").trim();
  const journeyAnchors = initialAnchorText
    ? {
        [initialRunId]: {
          text: initialAnchorText,
          dayNumber: Number(meta?.journeyAnchorDayNumber || 1),
          updatedAt: meta?.lastUpdatedAt || now,
        },
      }
    : {};

  const presenceMoments = Array.isArray(meta?.presenceMoments)
    ? meta.presenceMoments.map((item) => ({
        ...item,
        runId: normalizeRunId(item?.runId) || initialRunId,
      }))
    : [];

  const nextMeta = syncActiveRunMetaFields(
    {
      ...meta,
      runs: [
        {
          id: initialRunId,
          startedAt: meta?.createdAt || now,
          updatedAt: meta?.lastUpdatedAt || now,
          completedAt: completedDays.length >= totalDays ? meta?.lastUpdatedAt || now : null,
          status: completedDays.length >= totalDays ? "completed" : "active",
          ...summarizeIntake(legacyIntake || meta),
        },
      ],
      journeyAnchors,
      presenceMoments,
    },
    initialRunId
  );

  return { changed: true, meta: nextMeta };
}

const JourneyService = {
  /** Meta בסיסי למסע (מספר ימים, כותרת בעיה, תאריכים וכו׳) */
  getJourneyMeta(lang, problemKey) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey || PHASE1_REFERENCE_18_PROBLEM_KEY);

    const key = metaKey(safeLang, pk);
    const storedAny = findStoredValueForProblem(
      (candidateLang, candidateProblemKey) => metaKey(candidateLang, candidateProblemKey),
      candidateLangs(safeLang),
      pk
    );
    const stored = safeParse(storedAny?.raw || localStorage.getItem(key));

    const bp = getBlueprintForProblem(pk, safeLang);
    const totalDays = bp?.durationDays || bp?.totalDays || 7;
    const problemTitle = getProblemTitleForLang(pk, safeLang);

    const baseMeta = {
      lang: safeLang,
      problemKey: pk,
      blueprintId: bp?.id || null,
      totalDays,
      problemTitle,
    };

    const rawMeta = stored
      ? {
          ...baseMeta,
          ...stored,
          problemKey: pk,
          lang: safeLang,
          totalDays,
          problemTitle,
        }
      : {
          ...baseMeta,
          createdAt: new Date().toISOString(),
        };

    const ensured = ensureMetaRuns(safeLang, pk, rawMeta, totalDays);
    const meta = ensured.meta;
    localStorage.setItem(key, JSON.stringify(meta));
    return meta;
  },

  /** התחלת מסע חדש – שמירת אינטייק והגדרת meta */
  startNewJourney({ lang, problemKey, intake }) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const existingMeta = readMeta(safeLang, meta.problemKey) || meta;
    const runId = makeRunId();
    const now = new Date().toISOString();
    const keyMeta = metaKey(safeLang, meta.problemKey);
    const keyIntake = intakeKey(safeLang, meta.problemKey);

    const intakeToSave = {
      painNow: intake.painNow || "",
      desiredEndState: intake.desiredEndState || "",
      oneWord: intake.oneWord || "",
      entryGateKey: intake.entryGateKey || "",
      createdAt: now,
      totalDays: meta.totalDays,
    };

    const nextRuns = [
      ...(Array.isArray(existingMeta.runs) ? existingMeta.runs : []).map((run) =>
        normalizeRunId(run?.id) === normalizeRunId(existingMeta?.activeRunId) &&
        String(run?.status || "").toLowerCase() !== "completed"
          ? { ...run, status: "abandoned", updatedAt: now }
          : run
      ),
      {
        id: runId,
        startedAt: now,
        updatedAt: now,
        completedAt: null,
        status: "active",
        ...summarizeIntake(intakeToSave),
      },
    ];
    const previousAnchors =
      existingMeta?.journeyAnchors && typeof existingMeta.journeyAnchors === "object"
        ? existingMeta.journeyAnchors
        : {};
    const metaToSave = syncActiveRunMetaFields(
      {
        ...meta,
        ...existingMeta,
        entryGateKey: intake.entryGateKey || existingMeta.entryGateKey || "",
        activeRunId: runId,
        runs: nextRuns,
        journeyAnchors: previousAnchors,
        presenceMoments: Array.isArray(existingMeta.presenceMoments) ? existingMeta.presenceMoments : [],
        lastPresenceMoment: existingMeta.lastPresenceMoment || null,
        lastUpdatedAt: now,
      },
      runId
    );

    localStorage.setItem(runIntakeKey(safeLang, meta.problemKey, runId), JSON.stringify(intakeToSave));
    localStorage.setItem(keyIntake, JSON.stringify(intakeToSave));
    localStorage.setItem(keyMeta, JSON.stringify(metaToSave));

    postJson("/upsert", {
      problemKey: meta.problemKey,
      lang: safeLang,
      totalDays: meta.totalDays,
      runId,
      intake: intakeToSave,
      meta: metaToSave,
    }).catch(() => {});

    postProgress("/start", {
      problemKey: meta.problemKey,
      lang: safeLang,
      totalDays: meta.totalDays,
      runId,
    }).catch(() => {});

    return { meta: metaToSave, intake: intakeToSave, runId };
  },

  getActiveRun(lang, problemKey) {
    const meta = this.getJourneyMeta(lang, problemKey);
    const activeRunId = resolveRunId(meta, meta?.activeRunId);
    const activeRun =
      (Array.isArray(meta?.runs) ? meta.runs : []).find((run) => normalizeRunId(run?.id) === activeRunId) ||
      null;
    return { meta, activeRunId, activeRun };
  },

  listJourneyRuns(lang, problemKey) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runs = Array.isArray(meta?.runs) ? meta.runs : [];
    return runs
      .map((run, index) => {
        const runId = normalizeRunId(run?.id);
        const intake = this.loadIntake(safeLang, meta.problemKey, { runId }).intake;
        const { days } = this.loadAllDayStates(safeLang, meta.problemKey, undefined, { runId });
        const snapshot = buildRunSnapshot(run, meta, days, intake);
        return {
          ...snapshot,
          id: runId,
          index: index + 1,
          active: runId === resolveRunId(meta, meta?.activeRunId),
          anchorText: String(meta?.journeyAnchors?.[runId]?.text || "").trim(),
        };
      })
      .sort((a, b) => String(b.updatedAt || b.startedAt || "").localeCompare(String(a.updatedAt || a.startedAt || "")));
  },

  setActiveRun(lang, problemKey, runId) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const nextRunId = resolveRunId(meta, runId);
    if (!nextRunId) {
      return { meta, activeRunId: resolveRunId(meta, meta?.activeRunId), intake: null };
    }
    const nextMeta = syncActiveRunMetaFields(
      {
        ...meta,
        lastUpdatedAt: new Date().toISOString(),
        runs: (Array.isArray(meta?.runs) ? meta.runs : []).map((run) => {
          const normalizedId = normalizeRunId(run?.id);
          const isTarget = normalizedId === nextRunId;
          const isCompleted = String(run?.status || "").toLowerCase() === "completed" || !!run?.completedAt;
          return {
            ...run,
            status: isCompleted ? "completed" : isTarget ? "active" : "abandoned",
            updatedAt: isTarget ? new Date().toISOString() : run?.updatedAt,
          };
        }),
      },
      nextRunId
    );
    writeMeta(safeLang, meta.problemKey, nextMeta);
    const intake = this.loadIntake(safeLang, meta.problemKey, { runId: nextRunId }).intake;
    if (intake) {
      try {
        localStorage.setItem(intakeKey(safeLang, meta.problemKey), JSON.stringify(intake));
      } catch {
        // ignore
      }
    }
    return { meta: nextMeta, activeRunId: nextRunId, intake };
  },

  /** טעינת אינטייק (טקסט וסליידרים) */
  loadIntake(lang, problemKey, options = {}) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runId = resolveRunId(meta, options.runId);
    const keyRunIntake = runId ? runIntakeKey(safeLang, meta.problemKey, runId) : intakeKey(safeLang, meta.problemKey);
    const keyIntake = intakeKey(safeLang, meta.problemKey);
    const storedAny =
      (runId
        ? findStoredValueForProblem(
            (candidateLang, candidateProblemKey) => runIntakeKey(candidateLang, candidateProblemKey, runId),
            candidateLangs(safeLang),
            meta.problemKey
          )
        : null) ||
      findStoredValueForProblem(
        (candidateLang, candidateProblemKey) => intakeKey(candidateLang, candidateProblemKey),
        candidateLangs(safeLang),
        meta.problemKey
      );
    const intake = safeParse(storedAny?.raw || localStorage.getItem(keyRunIntake) || localStorage.getItem(keyIntake));
    if (storedAny && storedAny.key !== keyRunIntake && intake) {
      try {
        localStorage.setItem(keyRunIntake, JSON.stringify(intake));
      } catch {
        // ignore
      }
    }
    if (intake) {
      try {
        localStorage.setItem(keyIntake, JSON.stringify(intake));
      } catch {
        // ignore
      }
    }
    return { meta, intake, activeRunId: runId };
  },

  /** שמירת מצב של יום בודד */
  saveDayState(lang, problemKey, day, state, options = {}) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runId = resolveRunId(meta, options.runId);
    const key = runDayKey(safeLang, meta.problemKey, runId, day);

    // Respect the richer V2 payload coming from JourneyDay (journeyV2 holds the full form state)
    const journeyData = state.journeyV2 || state;
    const now = new Date().toISOString();

    const toSave = {
      day,
      checkInNote: journeyData?.checkInNote || state.checkInNote || "",
      stepTexts: journeyData?.stepTexts || state.stepTexts || {},
      journalEntries: state.journalEntries || journeyData?.journalEntries || [],
      presenceMoment: state.presenceMoment || journeyData?.presenceMoment || null,
      journeyV2: journeyData,
      currentStepIndex: Number(state.currentStepIndex ?? journeyData?.currentStepIndex ?? 0),
      safetyDismissed: !!(state.safetyDismissed ?? journeyData?.safetyDismissed),
      completed: !!(state.completed ?? journeyData?.completed),
      updatedAt: now,
    };

    localStorage.setItem(key, JSON.stringify(toSave));
    const nextRuns = (Array.isArray(meta?.runs) ? meta.runs : []).map((run) =>
      normalizeRunId(run?.id) === runId
        ? {
            ...run,
            updatedAt: now,
            status:
              toSave.completed && Number(day) >= Number(meta?.totalDays || 0)
                ? "completed"
                : run?.status || "active",
            completedAt:
              toSave.completed && Number(day) >= Number(meta?.totalDays || 0)
                ? now
                : run.completedAt || null,
          }
        : run
    );
    writeMeta(
      safeLang,
      meta.problemKey,
      syncActiveRunMetaFields(
        {
          ...meta,
          runs: nextRuns,
          lastUpdatedAt: now,
        },
        runId
      )
    );

    postJson("/day", {
      problemKey: meta.problemKey,
      lang: safeLang,
      totalDays: meta.totalDays,
      dayNumber: day,
      runId,
      data: toSave,
    }).catch(() => {});

    if (toSave.completed) {
      const note = String(toSave.checkInNote || journeyData?.checkInNote || "").trim();
      const journalLen = Array.isArray(toSave.journalEntries) ? toSave.journalEntries.length : 0;
      const stepTexts = journeyData?.stepTexts && typeof journeyData.stepTexts === "object" ? journeyData.stepTexts : {};
      const wroteSteps = Object.values(stepTexts).some(
        (v) => String(v || "").trim().length >= 40
      );
      postProgress("/complete-day", {
        problemKey: meta.problemKey,
        lang: safeLang,
        dayNumber: day,
        totalDays: meta.totalDays,
        runId,
        presenceMoment: toSave.presenceMoment || null,
        hadSignificantWriting:
          journalLen > 0 || note.length >= 120 || wroteSteps || Boolean(journeyData?.hadSignificantWriting),
        emotionalLoadDay: Boolean(journeyData?.emotionalLoadDay),
      }).catch(() => {});
    }
  },

  /** איפוס יום בודד */
  clearDayState(lang, problemKey, day, options = {}) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runId = resolveRunId(meta, options.runId);
    const key = runDayKey(safeLang, meta.problemKey, runId, day);
    localStorage.removeItem(key);
    try {
      localStorage.removeItem(dayKey(safeLang, meta.problemKey, day));
    } catch {
      // ignore
    }
    postJson("/day/reset", {
      problemKey: meta.problemKey,
      lang: safeLang,
      dayNumber: Number(day || 0),
      runId,
    }).catch(() => {});
  },

  /** טעינת יום בודד */
  getDayState(lang, problemKey, day, options = {}) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const explicitRunId = normalizeRunId(options.runId);
    const runId = resolveRunId(meta, options.runId);
    const key = runDayKey(safeLang, meta.problemKey, runId, day);
    const storedAny =
      findStoredValueForProblem(
        (candidateLang, candidateProblemKey) => runDayKey(candidateLang, candidateProblemKey, runId, day),
        candidateLangs(safeLang),
        meta.problemKey
      ) ||
      (!explicitRunId
        ? findStoredValueForProblem(
            (candidateLang, candidateProblemKey) => dayKey(candidateLang, candidateProblemKey, day),
            candidateLangs(safeLang),
            meta.problemKey
          )
        : null);
    const parsed = safeParse(storedAny?.raw || localStorage.getItem(key), null);
    if (storedAny && storedAny.key !== key && parsed) {
      try {
        localStorage.setItem(key, JSON.stringify(parsed));
      } catch {
        // ignore
      }
    }
    return parsed;
  },

  /** טעינת כל הימים הקיימים (1..totalDays) */
  loadAllDayStates(lang, problemKey, totalDaysOverride, options = {}) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runId = resolveRunId(meta, options.runId);
    const totalDays = totalDaysOverride || meta.totalDays || 7;
    const days = {};

    for (let d = 1; d <= totalDays; d++) {
      const state = this.getDayState(safeLang, meta.problemKey, d, { runId });
      if (state) days[d] = state;
    }

    return { meta, days, activeRunId: runId };
  },

  /** ימים שסומנו כ"יום הושלם" */
  getCompletedDays(lang, problemKey, options = {}) {
    const { meta, days, activeRunId } = this.loadAllDayStates(lang, problemKey, undefined, options);
    const completed = Object.entries(days)
      .filter(([, st]) => st && st.completed)
      .map(([d]) => Number(d));
    return { meta, completed, activeRunId };
  },

  /** היומן המלא – אינטייק + כל הימים */
  /** Create / load a stable AI session id per journey */
  getOrCreateAiSessionId(lang, problemKey, options = {}) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runId = resolveRunId(meta, options.runId);
    const pk = normalizeJourneyProblemKey(meta.problemKey);
    const key = runAiSessionKey(safeLang, pk, runId);

    let sid = localStorage.getItem(key);
    if (!sid) {
      sid =
        "sess_" +
        Date.now().toString(36) +
        "_" +
        Math.random().toString(36).slice(2, 8);
      localStorage.setItem(key, sid);
      try {
        localStorage.setItem(aiSessionKey(safeLang, pk), sid);
      } catch {
        // ignore
      }
    }

    return sid;
  },

  getFullJournal(lang, problemKey, options = {}) {
    const safeLang = normalizeLang(lang);
    const { meta, days, activeRunId } = this.loadAllDayStates(safeLang, problemKey, undefined, options);
    const entries = [];

    Object.keys(days)
      .map((d) => Number(d))
      .sort((a, b) => a - b)
      .forEach((d) => {
        const st = days[d] || {};
        const journalEntries = Array.isArray(st.journalEntries) ? st.journalEntries : [];
        journalEntries
          .filter((entry) => entry && entry.text && String(entry.text).trim())
          .forEach((entry) => {
            entries.push({
              dayNumber: d,
              promptNumber: Number(entry.promptNumber || 0) || null,
              questionId: entry.questionId || entry.promptId || "",
              questionText: entry.questionText || "",
              text: entry.text,
              createdAt: entry.createdAt || st.updatedAt || null,
              updatedAt: entry.updatedAt || st.updatedAt || null,
              lang: entry.lang || safeLang,
              problemKey: normalizeJourneyProblemKey(entry.problemKey || meta.problemKey),
              runId: activeRunId,
            });
          });
      });

    return { meta, entries, activeRunId };
  },

  mergeJournalEntriesIntoLocal(lang, problemKey, entries, options = {}) {
    if (!Array.isArray(entries) || entries.length === 0) return;
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const pk = normalizeJourneyProblemKey(meta.problemKey);
    const runId = resolveRunId(meta, options.runId);
    const grouped = new Map();

    entries.forEach((entry) => {
      const dayNumber = Number(entry?.dayNumber || 0);
      if (!dayNumber) return;
      if (!grouped.has(dayNumber)) grouped.set(dayNumber, []);
      grouped.get(dayNumber).push(entry);
    });

    grouped.forEach((dayEntries, dayNumber) => {
      const key = runDayKey(safeLang, pk, runId, dayNumber);
      const existing = safeParse(localStorage.getItem(key), {});
      const currentList = Array.isArray(existing.journalEntries) ? existing.journalEntries : [];
      const seen = new Set(currentList.map(buildJournalEntryKey));
      const merged = [...currentList];

      dayEntries.forEach((entry) => {
        const normalized = {
          dayNumber,
          promptNumber: Number(entry.promptNumber || 0) || null,
          questionId: entry.questionId || "",
          questionText: entry.questionText || "",
          text: entry.text || "",
          createdAt: entry.createdAt || entry.updatedAt || new Date().toISOString(),
          updatedAt: entry.updatedAt || entry.createdAt || new Date().toISOString(),
          lang: entry.lang || safeLang,
          problemKey: entry.problemKey || pk,
          runId,
        };
        const keyId = buildJournalEntryKey(normalized);
        if (seen.has(keyId)) return;
        seen.add(keyId);
        merged.push(normalized);
      });

      localStorage.setItem(
        key,
        JSON.stringify({
          ...(existing || {}),
          journalEntries: merged,
          updatedAt: new Date().toISOString(),
        })
      );
    });
  },

  async fetchJournalRecent(lang, problemKey, limit = 200) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    const data = await getJournal(`/recent?problemKey=${encodeURIComponent(pk)}&lang=${safeLang}&limit=${limit}`);
    return data?.entries || [];
  },

  async syncFromServer() {
    const data = await getJson("");
    if (!data || !Array.isArray(data.journeys)) return null;

    data.journeys.forEach((journey) => {
      const safeLang = normalizeLang(journey.lang);
      const pk = normalizeJourneyProblemKey(journey.problemKey);
      const journeyMeta = this.getJourneyMeta(safeLang, pk);
      const runId = resolveRunId(journeyMeta, journeyMeta?.activeRunId);
      const keyMeta = metaKey(safeLang, pk);
      const keyIntake = intakeKey(safeLang, pk);
      const problemTitle = getProblemTitleForLang(pk, safeLang);

      const metaToSave = syncActiveRunMetaFields(
        {
          ...journeyMeta,
          lang: safeLang,
          problemKey: pk,
          blueprintId: journey.meta?.blueprintId || journeyMeta?.blueprintId || null,
          totalDays: journey.totalDays || journeyMeta?.totalDays || 7,
          problemTitle,
          ...(journey.meta || {}),
          lastUpdatedAt: new Date().toISOString(),
        },
        runId
      );

      localStorage.setItem(keyMeta, JSON.stringify(metaToSave));
      if (journey.intake) {
        localStorage.setItem(runIntakeKey(safeLang, pk, runId), JSON.stringify(journey.intake));
        localStorage.setItem(keyIntake, JSON.stringify(journey.intake));
      }

      if (Array.isArray(journey.days)) {
        journey.days.forEach((day) => {
          const key = runDayKey(safeLang, pk, runId, day.dayNumber);
          localStorage.setItem(key, JSON.stringify(day.data || {}));
        });
      }
    });

    return data.journeys.length;
  },

  async fetchProgress(lang, problemKey) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    return getProgress(`/status?problemKey=${encodeURIComponent(pk)}&lang=${safeLang}`);
  },

  async saveSignals({ lang, problemKey, dayNumber, mood, energy, totalDays }) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    const latest = writeLatestSignalsCache(safeLang, pk, {
      dayNumber: Number(dayNumber || 0) || null,
      mood: Number(mood || 0) || null,
      energy: Number(energy || 0) || null,
      totalDays: Number(totalDays || 0) || null,
      updatedAt: new Date().toISOString(),
    });
    const response = await postSignals("/save", {
      problemKey: pk,
      lang: safeLang,
      dayNumber,
      mood,
      energy,
      totalDays,
    });
    return response || { ok: true, cached: true, latest };
  },

  getLatestSignals(lang, problemKey) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    return readLatestSignalsCache(safeLang, pk);
  },

  async fetchLatestSignals(lang, problemKey) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    const cached = readLatestSignalsCache(safeLang, pk);
    const data = await getSignals(`/latest?problemKey=${encodeURIComponent(pk)}&lang=${safeLang}`);
    const latest = data?.latest || null;
    if (latest && typeof latest === "object") {
      return writeLatestSignalsCache(safeLang, pk, latest);
    }
    return cached;
  },

  async fetchInsights(lang, problemKey) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    return getInsights(`/weekly?problemKey=${encodeURIComponent(pk)}&lang=${safeLang}`);
  },

  savePresenceMoment({ lang, problemKey, dayNumber, emotionalStage, echo }) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runId = resolveRunId(meta, meta?.activeRunId);
    const existingMeta = readMeta(safeLang, meta.problemKey) || meta;
    const presenceMoment = {
      date: new Date().toISOString(),
      journeyKey: meta.problemKey,
      runId,
      day: Number(dayNumber || 0),
      emotionalStage: emotionalStage || "pause",
      echo: echo || "",
    };
    const existingMoments = Array.isArray(existingMeta.presenceMoments)
      ? existingMeta.presenceMoments
      : [];
    const nextMoments = existingMoments
      .filter((item) => !(String(item?.journeyKey) === meta.problemKey && Number(item?.day) === Number(dayNumber)))
      .concat(presenceMoment)
      .sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));

    writeMeta(safeLang, meta.problemKey, {
      ...existingMeta,
      presenceMoments: nextMoments,
      lastPresenceMoment: presenceMoment,
      lastUpdatedAt: new Date().toISOString(),
    });

    return presenceMoment;
  },

  saveJourneyAnchor({ lang, problemKey, text, dayNumber = 1, promptNumber = null }) {
    const safeLang = normalizeLang(lang);
    const meta = this.getJourneyMeta(safeLang, problemKey);
    const runId = resolveRunId(meta, meta?.activeRunId);
    const existingMeta = readMeta(safeLang, meta.problemKey) || meta;
    const anchorText = String(text || "").trim();
    if (!anchorText) {
      return "";
    }

    const nextMeta = syncActiveRunMetaFields(
      {
        ...existingMeta,
        journeyAnchors: {
          ...(existingMeta?.journeyAnchors && typeof existingMeta.journeyAnchors === "object"
            ? existingMeta.journeyAnchors
            : {}),
          [runId]: {
            text: anchorText,
            dayNumber: Number(dayNumber || 1),
            sourceDayNumber: Number(dayNumber || 1),
            sourcePromptNumber: Number(promptNumber || 0) || null,
            updatedAt: new Date().toISOString(),
          },
        },
        lastUpdatedAt: new Date().toISOString(),
      },
      runId
    );

    writeMeta(safeLang, meta.problemKey, nextMeta);

    postJson("/upsert", {
      problemKey: meta.problemKey,
      lang: safeLang,
      totalDays: meta.totalDays,
      runId,
      meta: nextMeta,
    }).catch(() => {});

    return anchorText;
  },

  getJourneyAnchor(lang, problemKey) {
    const safeLang = normalizeLang(lang || "he");
    const meta = this.getJourneyMeta(safeLang, normalizeJourneyProblemKey(problemKey || PHASE1_REFERENCE_18_PROBLEM_KEY));
    const runId = resolveRunId(meta, meta?.activeRunId);
    const anchor = meta?.journeyAnchors?.[runId];
    return String(anchor?.text || meta?.journeyAnchorText || "").trim();
  },

  getPresenceMoments(lang, problemKey, options = {}) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    const meta = readMeta(safeLang, pk);
    const runId = normalizeRunId(options.runId);
    const moments = Array.isArray(meta?.presenceMoments) ? meta.presenceMoments : [];
    return runId ? moments.filter((item) => normalizeRunId(item?.runId) === runId) : moments;
  },

  getLatestPresenceMoment(lang, problemKey) {
    const safeLang = normalizeLang(lang || "he");
    const pk = normalizeJourneyProblemKey(problemKey);
    const moments = candidateLangs(safeLang)
      .flatMap((candidate) => {
        const meta = readMeta(candidate, pk);
        return Array.isArray(meta?.presenceMoments) ? meta.presenceMoments : [];
      })
      .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    return moments[0] || null;
  },

  getReturnTarget(lang, problemKey, options = {}) {
    const safeLang = normalizeLang(lang || "he");
    const pk = normalizeJourneyProblemKey(problemKey);
    const { meta, days, activeRunId } = this.loadAllDayStates(safeLang, pk, undefined, options);
    const totalDays = Number(meta?.totalDays || 0) || 1;
    const completedDays = Object.entries(days || {})
      .filter(([, state]) => state?.completed)
      .map(([day]) => Number(day));
    const highestCompleted = completedDays.length ? Math.max(...completedDays) : 0;
    const nextDay = Math.min(totalDays, highestCompleted + 1 || 1);
    if (!highestCompleted) {
      return `/journey/day-dna?problemKey=${encodeURIComponent(pk)}&day=1&lang=${safeLang}&runId=${encodeURIComponent(activeRunId)}`;
    }
    if (highestCompleted >= totalDays) {
      return `/journey/summary-final?problemKey=${encodeURIComponent(pk)}&lang=${safeLang}&runId=${encodeURIComponent(activeRunId)}`;
    }
    return `/journey/day-dna?problemKey=${encodeURIComponent(pk)}&day=${nextDay}&lang=${safeLang}&runId=${encodeURIComponent(activeRunId)}`;
  },

  getOverviewTarget(lang, problemKey, options = {}) {
    const safeLang = normalizeLang(lang || "he");
    const pk = normalizeJourneyProblemKey(problemKey);
    const meta = this.getJourneyMeta(safeLang, pk);
    const runId = resolveRunId(meta, options.runId || meta?.activeRunId);
    return `/journey/overview?problemKey=${encodeURIComponent(pk)}&lang=${safeLang}&runId=${encodeURIComponent(runId)}`;
  },

  getCompanionTarget(lang, problemKey, options = {}) {
    const safeLang = normalizeLang(lang || "he");
    const pk = normalizeJourneyProblemKey(problemKey);
    const requestedDay = Number(options?.day || 0);
    const { meta, days, activeRunId } = this.loadAllDayStates(safeLang, pk, undefined, options);
    const totalDays = Number(meta?.totalDays || 0) || 1;
    const completedDays = Object.entries(days || {})
      .filter(([, state]) => state?.completed)
      .map(([day]) => Number(day));
    const highestCompleted = completedDays.length ? Math.max(...completedDays) : 0;
    const fallbackDay = highestCompleted >= totalDays ? totalDays : Math.min(totalDays, highestCompleted + 1 || 1);
    const targetDay = requestedDay > 0 ? Math.min(totalDays, requestedDay) : fallbackDay;
    return `/voice-chat?problemKey=${encodeURIComponent(pk)}&day=${targetDay}&lang=${safeLang}&runId=${encodeURIComponent(activeRunId)}`;
  },

  /**
   * נתוני מסע נשמרים ב-localStorage לפי שפה+נושא בלבד (ללא userId).
   * בהחלפת משתמש באותו דפדפן/WebView חייבים לנקות לפני סנכרון — אחרת יופיעו מסעות מהחשבון הקודם.
   */
  clearLocalJourneyCache() {
    if (typeof localStorage === "undefined") return;
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(`${STORAGE_PREFIX}::`)) toRemove.push(key);
    }
    toRemove.forEach((key) => localStorage.removeItem(key));
  },

  /** מוחק מ-localStorage את כל המפתחות של נושא מסוים (שפה + וריאנטים של מפתח). */
  clearLocalJourneyBranch(lang, problemKey) {
    if (typeof localStorage === "undefined") return;
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    const rawPk = String(problemKey || "").trim();
    const variants = uniqueList([
      pk,
      rawPk,
      ...(pk === PHASE1_REFERENCE_18_PROBLEM_KEY ? ["rel_breakup_fresh_pain", PHASE1_PROBLEM_KEY] : []),
    ]);
    const langs = candidateLangs(safeLang);
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(`${STORAGE_PREFIX}::`)) continue;
      const hit = langs.some((L) =>
        variants.some((variant) => key.startsWith(`${STORAGE_PREFIX}::${L}::${variant}::`))
      );
      if (hit) toRemove.push(key);
    }
    uniqueList(toRemove).forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch {
        /* ignore */
      }
    });
  },

  /**
   * אם נבחר שער "משהו אחר" בלי מסע ממשי ועדיין אין התקדמות בימים — ניקוי branch כדי שלא יישאר resume שבור.
   */
  clearPlaceholderPhaseOneStartIfAny(lang, problemKey) {
    const safeLang = normalizeLang(lang);
    const pk = normalizeJourneyProblemKey(problemKey);
    const { intake } = this.loadIntake(safeLang, pk);
    if (!isPhaseOneOtherIntakeSnapshot(intake, safeLang)) return;
    const { days } = this.loadAllDayStates(safeLang, pk);
    if (Object.keys(days || {}).length > 0) return;
    this.clearLocalJourneyBranch(safeLang, pk);
  },

  getMostRecentJourney(lang) {
    const safeLang = normalizeLang(lang || "he");
    const metas = [];
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || !key.startsWith(`${STORAGE_PREFIX}::`)) continue;
      if (!key.endsWith("::meta")) continue;
      const raw = localStorage.getItem(key);
      const parsed = safeParse(raw, null);
      if (!parsed?.problemKey) continue;
      const normalizedProblemKey = normalizeJourneyProblemKey(parsed.problemKey);
      const meta = this.getJourneyMeta(safeLang, normalizedProblemKey);
      const runId = resolveRunId(meta, meta?.activeRunId);
      const activeRun = (Array.isArray(meta?.runs) ? meta.runs : []).find(
        (run) => normalizeRunId(run?.id) === runId
      );
      metas.push({
        problemKey: normalizedProblemKey,
        lang: parsed.lang || safeLang,
        updatedAt: activeRun?.updatedAt || meta?.lastUpdatedAt || meta?.createdAt || "",
      });
    }
    return metas.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0] || null;
  },

  getResumeJourney(lang) {
    const safeLang = normalizeLang(lang || "he");
    const problemKeys = new Set();

    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (!key || !key.startsWith(`${STORAGE_PREFIX}::`) || !key.endsWith("::meta")) continue;
      const raw = localStorage.getItem(key);
      const parsed = safeParse(raw, null);
      if (parsed?.problemKey) {
        problemKeys.add(normalizeJourneyProblemKey(parsed.problemKey));
      }
    }

    const candidates = [];
    problemKeys.forEach((problemKey) => {
      const meta = this.getJourneyMeta(safeLang, problemKey);
      const runs = Array.isArray(meta?.runs) ? meta.runs : [];

      runs.forEach((run) => {
        const runId = normalizeRunId(run?.id);
        if (!runId) return;
        const intake = this.loadIntake(safeLang, problemKey, { runId }).intake;
        const { days } = this.loadAllDayStates(safeLang, problemKey, undefined, { runId });
        const snapshot = buildRunSnapshot(run, meta, days, intake);
        if (snapshot.isComplete) return;

        candidates.push({
          problemKey: meta.problemKey,
          runId,
          currentDay: snapshot.nextDay,
          updatedAt: String(snapshot.updatedAt || snapshot.startedAt || meta?.lastUpdatedAt || meta?.createdAt || ""),
          title: getProblemTitleForLang(meta.problemKey, safeLang, { includeDuration: false }),
        });
      });
    });

    return candidates.sort((a, b) => String(b.updatedAt).localeCompare(String(a.updatedAt)))[0] || null;
  },
};

export default JourneyService;
